import { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  Typography,
  Button,
  TextField,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  DialogContentText,
  Grid,
  Tooltip,
  Box,
  Stack,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupIcon from '@mui/icons-material/Group';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { enqueueSnackbar } from '../../../../reusable-components/snackbar';
import { AwaitButton } from '../../../../reusable-components/await-button';
import SimpleAutocompleteProps from '../../../../reusable-components/autocomplete/SimpleAutocompleteProps';
import axiosInstance from '../../../../utils/axios';
import { objectToFormData } from '../../../../api/utils';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import AddOptionAutocompleteProps from '../../../../reusable-components/autocomplete/AddOptionAutocompleteProps';
import FileUploadButton from '../../../../reusable-components/basic-buttons/FileUploadButton';
import DeleteIconButton from '../../../../reusable-components/basic-buttons/DeleteIconButton';
import { download } from '../../../../utils/download';
import { getVendorPriceAccounts } from '../../../../api/vendors';

FacilityAccountForm.propTypes = {
  close: PropTypes.func,
  openModal: PropTypes.bool,
  editAccount: PropTypes.object,
  vendors: PropTypes.array,
  facilities: PropTypes.array,
  setFacilityAccounts: PropTypes.func,
};

export default function FacilityAccountForm({
  openModal,
  close,
  editAccount,
  vendors,
  facilities,
  setFacilityAccounts,
}) {
  const [attachment, setAttachment] = useState(null);
  const [value, setValue] = useState({});
  const [extraFields, setExtraFields] = useState([]);
  const [loadingExtraFields, setLoadingExtraFields] = useState(false);
  const [netTermOptions, setNetTermOptions] = useState([
    { value: 'Net 7', label: 'Net 7' },
    { value: 'Net 14', label: 'Net 14' },
    { value: 'Net 15', label: 'Net 15' },
    { value: 'Net 30', label: 'Net 30' },
    { value: 'Net 60', label: 'Net 60' },
    { value: 'Net 90', label: 'Net 90' },
  ]);
  const [vendorPriceAccounts, setVendorPriceAccounts] = useState([]);
  const [defaultVendorPriceAccount, setDefaultVendorPriceAccount] = useState(null);

  const handleSave = () => {
    axiosInstance
      .post(
        'FacilityAccount/FacilityVendorAccountForm',
        objectToFormData({
          id: editAccount?.id,
          facilityId: value.facility.value,
          vendorId: value.vendor.value,
          account: value.account,
          priceAccountId: value.vendorPriceAccount,
          paymentTerms: value.paymentTerms,
          ppd: value.isPPD || false,
          orderingDisabled: value.orderingDisabled || false,
          invoicingDisabled: value.invoicingDisabled || false,
          invoicingUpdateOrdersOnly: value.invoicingUpdateOrdersOnly || false,
          onHold: value.onHold || false,
          file: value.file,
          fileName: value.file?.attachment ?? value.file?.name,
          extraValues: extraFields
            .filter((field) => field.value != null)
            .map((field) => ({
              key: field.id,
              value: field.value,
            })),
        })
      )
      .then((res) => {
        if (!res.data.error) {
          enqueueSnackbar(`Vendor account ${editAccount ? 'edited' : 'added'} successfully`, { variant: 'success' });
          setFacilityAccounts((accounts) =>
            editAccount
              ? [...accounts.map((account) => (account.id === res.data.account.id ? res.data.account : account))]
              : [...accounts, res.data.account]
          );
          handleClose();
        } else
          enqueueSnackbar(`Failed to ${editAccount ? 'edit' : 'add'} vendor account, - ${res.data.error} `, {
            variant: 'error',
          });
      })
      .catch(() => enqueueSnackbar(`Failed to ${editAccount ? 'edit' : 'add'} vendor account`, { variant: 'error' }))
  };

  const getExtraFields = () => {
    setLoadingExtraFields(true);
    axiosInstance
      .get(`FacilityAccount/AccountExtraFields?vendorId=${value.vendor.value}&accountId=${editAccount?.id}`)
      .then((res) => setExtraFields(res.data ?? []))
      .catch(() => {
        setExtraFields([]);
        enqueueSnackbar('Failed to load extra account fields', { variant: 'error' });
      })
      .finally(() => setLoadingExtraFields(false));
  };

  useEffect(() => {
    if (value.vendor) {
      getExtraFields();
      (async () => {
        try {
          const res = await getVendorPriceAccounts(value.vendor.value);
          setVendorPriceAccounts(res.data.vendorPriceAccounts ?? []);
          setDefaultVendorPriceAccount(res.data.defaultAccountNumber ?? null);
        } catch (error) {
          enqueueSnackbar('Failed to load vendor price accounts', { variant: 'error' });
        }
      })();
    }
    else {
      setVendorPriceAccounts([]);
      setDefaultVendorPriceAccount(null);
    }
  }, [value.vendor]);

  useEffect(() => {
    setAttachment(!editAccount ? null : editAccount.fileName ? { attachment: editAccount.fileName } : null);
    setValue(
      editAccount
        ? {
            facility: { value: editAccount.facilityId, label: editAccount.facility },
            vendor: { value: editAccount.vendorId, label: editAccount.vendor },
            account: editAccount.account,
            paymentTerms: editAccount.paymentTerms,
            isPPD: editAccount.isPPD,
            orderingDisabled: editAccount.orderingDisabled,
            invoicingDisabled: editAccount.invoicingDisabled,
            invoicingUpdateOrdersOnly: editAccount.invoicingUpdateOrdersOnly,
            onHold: editAccount.onHold,
            file: editAccount.fileName ? { attachment: editAccount.fileName } : '',
            vendorPriceAccount: editAccount.priceAccountId,
          }
        : {}
    );
  }, [editAccount]);

  const handleClose = () => {
    close();
    setValue({});
    setExtraFields([]);
  };

  const sortedVendors = useMemo(
    () => [...vendors].filter((v) => v?.label).sort((a, b) => a.label.localeCompare(b.label)),
    [vendors]
  );

  return (
    <Dialog open={openModal} onClose={(event, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      handleClose();
    }} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center' }}>
        {editAccount ? 'Edit' : 'Add'} Facility Account <CloseIconButton onClick={handleClose} />
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">Facility*</Typography>
            <Autocomplete
              {...SimpleAutocompleteProps}
              renderInput={(params) => (
                <TextField
                  {...params}
                  type="search"
                  inputProps={{ ...params.inputProps }}
                  placeholder="Search or select"
                  autoComplete="off"
                />
              )}
              value={value.facility || ''}
              options={facilities}
              onChange={(event, newValue) => {
                setExtraFields([]);
                setValue((prev) => ({ ...prev, facility: newValue }));
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">Vendor*</Typography>
            <Autocomplete
              {...SimpleAutocompleteProps}
              renderInput={(params) => (
                <TextField
                  {...params}
                  type="search"
                  inputProps={{ ...params.inputProps }}
                  placeholder="Search or select"
                  autoComplete="off"
                />
              )}
              value={value.vendor || ''}
              options={sortedVendors}
              onChange={(event, newValue) => {
                setExtraFields([]);
                setValue((prev) => ({ ...prev, vendor: newValue }));
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">Payment Terms</Typography>
            <Autocomplete
              {...SimpleAutocompleteProps}
              {...AddOptionAutocompleteProps}
              renderInput={(params) => (
                <TextField
                  {...params}
                  type="search"
                  inputProps={{ ...params.inputProps }}
                  placeholder="Search or select"
                  autoComplete="off"
                />
              )}
              value={netTermOptions.find((term) => term.label === value.paymentTerms) ?? null}
              options={netTermOptions}
              onChange={(event, newValue) => {
                if (typeof newValue === 'string') setValue({ ...value, paymentTerms: newValue });
                else if (newValue && newValue.inputValue) {
                  setNetTermOptions((opts) => [...opts, { value: newValue.inputValue, label: newValue.inputValue }]);
                  setValue({ ...value, paymentTerms: newValue.inputValue });
                } else setValue({ ...value, paymentTerms: newValue?.label });
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Tooltip title={value?.vendor?.label === 'Amazon' ? 'From Identity' : ''} placement="top" arrow>
              <Typography variant="subtitle1">Account Number*</Typography>
            </Tooltip>
            <TextField
              size="small"
              fullWidth
              value={value.account ?? ''}
              onChange={(e) => setValue((prev) => ({ ...prev, account: e.target.value }))}
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
              Vendor Price Account
              <Tooltip title={`Account to determine pricing tier.`} placement="top" arrow>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main', ml: 0.5}} />
              </Tooltip>
            </Typography>
            <Autocomplete
              size="small"
              fullWidth
              options={vendorPriceAccounts || []}
              getOptionLabel={(option) => option.description ? `${option.accountNumber} - ${option.description}` : option.accountNumber || ''}
              value={vendorPriceAccounts?.find((account) => account.id === value.vendorPriceAccount) || (defaultVendorPriceAccount ? {description: '', accountNumber: defaultVendorPriceAccount || '', id: defaultVendorPriceAccount || ''} : '')}
              onChange={(e, newValue) => {
                setValue((prev) => ({ ...prev, vendorPriceAccount: newValue ? newValue.id : '' }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoComplete="off"
                />
              )}
            />
          </Grid>
          {extraFields.length > 0 && (
            <Grid item xs={12}>
              <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                {extraFields.map((field, index) => {
                  const { encrypt, hoverText, label, id, required } = field;
                  return (
                    <Grid
                      key={id}
                      item
                      xs={
                        extraFields.length === 1
                          ? 12
                          : extraFields.length - 1 > index || extraFields.length % 2 === 0
                          ? 6
                          : 12
                      }
                    >
                      <Tooltip title={hoverText} placement="top" arrow>
                        <Typography variant="subtitle1">
                          {label}
                          {required ? '*' : ''}
                        </Typography>
                      </Tooltip>
                      <TextField
                        inputProps={encrypt ? { style: { WebkitTextSecurity: 'disc' } } : {}}
                        size="small"
                        fullWidth
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const newExtraFields = [...extraFields];
                          newExtraFields[index].value = e.target.value;
                          setExtraFields(newExtraFields);
                        }}
                        required={required}
                        autoComplete="off"
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          )}
          <Grid item>
            <FileUploadButton
              handleFileDrop={(file) => {
                setAttachment(file);
                setValue((prev) => ({ ...prev, file }));
              }}
            />
          </Grid>
          {attachment != null && value.file !== '' && (
            <Grid item display="flex" alignItems="center">
              <DeleteIconButton
                onClick={() => {
                  setAttachment(null);
                  setValue((prev) => ({ ...prev, file: '' }));
                }}
                tooltipText="Delete File"
                disableRipple
              />
              <Typography
                onClick={() => {
                  if (editAccount)
                    download(`/Api/FacilityAccount/GetFacilityAccountPDF?id=${editAccount.id}`, editAccount.fileName);
                }}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                {attachment.attachment ?? attachment.name}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} display="flex">
            <Tooltip
              title={
                'Enables Per Patient Day billing - used for facilities billed based on daily patient counts rather than itemized orders'
              }
              arrow
              placement="top"
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DialogContentText>PPD</DialogContentText>
                  <GroupIcon sx={{ fontSize: 20 }} color="disabled" />
                </Stack>
                <ToggleButtonGroup
                  value={value.isPPD ? 'true' : 'false'}
                  color="secondary"
                  exclusive
                  size="medium"
                  onChange={(event, newValue) =>
                    setValue((prev) => ({ ...prev, isPPD: newValue ? newValue === 'true' : prev.isPPD }))
                  }
                  aria-label="PPD selection"
                >
                  <ToggleButton sx={{ border: '1px solid rgb(34,34,34,.7)' }} value="true">
                    Yes
                  </ToggleButton>
                  <ToggleButton sx={{ border: '1px solid rgb(34,34,34,.7)' }} value="false">
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={12} display="flex">
            <Tooltip
              title={
                'When ordering is turned off, the system excludes the account number from orders and disables price updates for the account. ' +
                'If on hold is selected there will be a warning before submitting an order.'
              }
              arrow
              placement="top"
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DialogContentText>Allow Ordering</DialogContentText>
                  <ShoppingCartIcon sx={{ fontSize: 20 }} color="disabled" />
                </Stack>
                <ToggleButtonGroup
                  value={!value.orderingDisabled && !value.onHold ? 'yes' : value.onHold ? 'onHold' : 'no'}
                  exclusive
                  size="medium"
                  color="secondary"
                  onChange={(event, newValue) =>
                    setValue((prev) => ({
                      ...prev,
                      orderingDisabled: newValue ? newValue === 'no' : prev.orderingDisabled,
                      onHold: newValue ? newValue === 'onHold' : prev.onHold,
                    }))
                  }
                >
                  <ToggleButton sx={{ border: '1px solid rgb(34,34,34,.7)' }} value="yes">
                    Yes
                  </ToggleButton>
                  <ToggleButton sx={{ border: '1px solid rgb(34,34,34,.7)' }} value="onHold">
                    On Hold
                  </ToggleButton>
                  <ToggleButton sx={{ border: '1px solid rgb(34,34,34,.7)' }} value="no">
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={12} display="flex">
            <Tooltip
              title={
                'If invoicing is disabled for an account, incoming electronic invoices will be blocked. ' +
                'If update orders only is selected the invoices will automatically move to processed.'
              }
              arrow
              placement="top"
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DialogContentText>Allow Invoices</DialogContentText>
                  <RequestQuoteIcon sx={{ fontSize: 20 }} color="disabled" />
                </Stack>
                <ToggleButtonGroup
                  value={
                    !value.invoicingDisabled && !value.invoicingUpdateOrdersOnly
                      ? 'allowAll'
                      : value.invoicingUpdateOrdersOnly
                      ? 'invoicingUpdateOrdersOnly'
                      : 'disable'
                  }
                  exclusive
                  color="secondary"
                  variant="contained"
                  size="medium"
                  onChange={(event, newValue) =>
                    setValue((prev) => ({
                      ...prev,
                      invoicingDisabled: newValue ? newValue === 'disable' : prev.invoicingDisabled,
                      invoicingUpdateOrdersOnly: newValue
                        ? newValue === 'invoicingUpdateOrdersOnly'
                        : prev.invoicingUpdateOrdersOnly,
                    }))
                  }
                >
                  <ToggleButton sx={{ border: '1px solid rgb(34,34,34,.7)' }} value="allowAll">
                    Allow All
                  </ToggleButton>
                  <ToggleButton sx={{ border: '1px solid rgb(34,34,34,.7)' }} value="invoicingUpdateOrdersOnly">
                    Update Orders Only
                  </ToggleButton>
                  <ToggleButton sx={{ border: '1px solid rgb(34,34,34,.7)' }} value="disable">
                    Disable All
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>
        <AwaitButton
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={
            editAccount?.locked ||
            !value.facility ||
            !value.vendor ||
            !value.account ||
            loadingExtraFields ||
            extraFields.some((field) => field.required && !field.value)
          }
        >
          {editAccount ? 'Save' : 'Add Account'}
        </AwaitButton>
      </DialogActions>
    </Dialog>
  );
}
